import api from "../../core/api";
import i18n from "../../core/i18n";
import accountContext from "../../account/accountContext";
import {
  SendMessageRequest,
  SendMessageResponse,
  ChatHistoryResponse,
  SetContextRequest,
  SetContextResponse,
  ClearContextResponse,
  BotAccessResponse,
  HealthCheckResponse,
  ActionResultPayload,
} from "./models/chatBotModels";
import {
  SessionInitResponse,
  ThreadListResponse,
  ThreadDetailResponse,
  ThreadCountsResponse,
} from "../../customer/midas/midasPageModels";

const getRequestConfig = () => {
  return { baseURL: (window as any).app.config.apiBaseUrl };
};

class ChatBotApi {
  /**
   * Send a message and get a complete response (non-streaming).
   * POST /v1/bot/chat
   * When image is provided, sends as multipart/form-data instead of JSON.
   * When action_id is provided, includes it in the request body (action button click).
   * When actionResult is provided (MA-0b.3), includes it for conversation continuity.
   */
  sendMessage(
    message: string,
    machineId?: string | null,
    threadId?: string,
    image?: File,
    actionId?: string,
    actionContext?: Record<string, unknown>,
    actionResult?: ActionResultPayload,
    newThread?: boolean
  ): Promise<SendMessageResponse> {
    if (image) {
      // Multipart form-data for image upload
      const formData = new FormData();
      formData.append("message", message);
      formData.append("machine_id", machineId || "");
      if (threadId) {
        formData.append("thread_id", threadId);
      }
      if (newThread) {
        formData.append("new_thread", "true");
      }
      formData.append("image", image);
      return api
        .post<SendMessageResponse>("/v1/bot/chat", formData, {
          ...getRequestConfig(),
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((x) => x.data);
    }

    const body: Record<string, any> = { message };
    body.machine_id = machineId || null;
    if (threadId) {
      body.thread_id = threadId;
    }
    if (actionId) {
      body.action_id = actionId;
    }
    if (actionContext) {
      body.action_context = actionContext;
    }
    if (actionResult) {
      body.action_result = actionResult;
    }
    if (newThread) {
      body.new_thread = true;
    }
    return api
      .post<SendMessageResponse>("/v1/bot/chat", body, getRequestConfig())
      .then((x) => x.data);
  }

  /**
   * Send a message via SSE streaming for progress indicators.
   * POST /v1/bot/chat/stream
   *
   * Returns the raw Response object so the caller can read the SSE stream.
   * Uses native fetch (not axios) because we need access to ReadableStream
   * for Server-Sent Events parsing (axios buffers the response).
   *
   * When image is provided, sends as multipart/form-data instead of JSON.
   * When action_id is provided, includes it in the request body (action button click).
   * When actionResult is provided (MA-0b.3), includes it for conversation continuity.
   */
  async streamMessage(
    message: string,
    machineId?: string | null,
    threadId?: string,
    image?: File,
    actionId?: string,
    actionContext?: Record<string, unknown>,
    actionResult?: ActionResultPayload,
    newThread?: boolean,
    /** MC-26: Short friendly text to persist in ChatMessages instead of `message`.
     *  Bot uses the full `message` for prompt/context but stores `display_text`
     *  as the user-visible history. Optional; older bots ignore it. */
    displayText?: string,
    /** MC-27: Structured page hint for (i) button. Adds page_info_request to JSON body.
     *  Only applies on non-multipart (JSON) requests. */
    pageInfoRequest?: {
      page: number;
      collection: string;
      list_id?: string;
    }
  ): Promise<Response> {
    const baseUrl = (window as any).app.config.apiBaseUrl;
    const url = `${baseUrl}/v1/bot/chat/stream`;

    const token = accountContext.accessToken;

    let fetchBody: BodyInit;
    const headers: Record<string, string> = {
      Accept: "text/event-stream",
      "X-UI-Language": localStorage.getItem("i18nextLng") || "sk",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    if (image) {
      // Multipart form-data for image upload
      // Do NOT set Content-Type — browser sets it with boundary automatically
      const formData = new FormData();
      formData.append("message", message);
      formData.append("machine_id", machineId || "");
      if (threadId) {
        formData.append("thread_id", threadId);
      }
      if (newThread) {
        formData.append("new_thread", "true");
      }
      formData.append("image", image);
      fetchBody = formData;
    } else {
      headers["Content-Type"] = "application/json";
      const body: Record<string, any> = { message };
      body.machine_id = machineId || null;
      if (threadId) {
        body.thread_id = threadId;
      }
      if (actionId) {
        body.action_id = actionId;
      }
      if (actionContext) {
        body.action_context = actionContext;
      }
      if (actionResult) {
        body.action_result = actionResult;
      }
      if (newThread) {
        body.new_thread = true;
      }
      // MC-26: forward friendly-text override so the bot persists the short
      // display version in history instead of the raw info_prompt. Older bots
      // ignore unknown fields.
      if (displayText && displayText.length > 0) {
        body.display_text = displayText;
      }
      // MC-27: forward structured page hint from (i) button click. Bot uses it to
      // skip intent classification + vector search; older bots ignore unknown fields.
      if (pageInfoRequest) {
        body.page_info_request = pageInfoRequest;
      }
      fetchBody = JSON.stringify(body);
    }

    // Abort controller with 90s timeout to prevent indefinite hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90_000);

    try {
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: fetchBody,
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Stream request failed with status ${response.status}`);
      }

      return response;
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        throw new Error("Stream request timed out after 90 seconds");
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Get conversation history.
   * GET /v1/bot/chat/history
   */
  getHistory(limit?: number): Promise<ChatHistoryResponse> {
    const params: Record<string, unknown> = {};
    if (limit !== undefined) {
      params.limit = limit;
    }
    return api
      .get<ChatHistoryResponse>("/v1/bot/chat/history", {
        ...getRequestConfig(),
        params,
      })
      .then((x) => x.data);
  }

  /**
   * Set the machine context for the current session.
   * POST /v1/bot/chat/context
   * Pass machineId=null to exit machine mode (returns ClearContextResponse).
   */
  setMachineContext(machineId: string): Promise<SetContextResponse>;
  setMachineContext(machineId: null): Promise<ClearContextResponse>;
  setMachineContext(
    machineId: string | null
  ): Promise<SetContextResponse | ClearContextResponse> {
    const body: SetContextRequest = { machine_id: machineId };
    return api
      .post<SetContextResponse | ClearContextResponse>(
        "/v1/bot/chat/context",
        body,
        getRequestConfig()
      )
      .then((x) => x.data);
  }

  /**
   * Check if the bot server is healthy and reachable.
   * GET /v1/bot/health
   *
   * Uses native fetch instead of the global axios instance to avoid
   * triggering the global 403 handler (PermissionDeniedNotification).
   * These background checks should fail silently.
   */
  async checkHealth(): Promise<HealthCheckResponse> {
    const baseUrl = (window as any).app.config.apiBaseUrl;
    const token = accountContext.accessToken;
    const headers: Record<string, string> = {
      Accept: "application/json",
      "X-UI-Language": localStorage.getItem("i18nextLng") || "sk",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const response = await fetch(`${baseUrl}/v1/bot/health`, { headers });
    if (!response.ok) {
      throw new Error(`Health check failed: ${response.status}`);
    }
    return response.json();
  }

  /**
   * Check if the current user/company has access to the bot chat feature.
   * GET /v1/customer/settings/bot-chat
   *
   * Uses native fetch to avoid triggering the global 403 handler.
   * If the user lacks permission, we just return disabled -- no banner.
   */
  async checkBotAccess(): Promise<BotAccessResponse> {
    const baseUrl = (window as any).app.config.apiBaseUrl;
    const token = accountContext.accessToken;
    const headers: Record<string, string> = {
      Accept: "application/json",
      "X-UI-Language": localStorage.getItem("i18nextLng") || "sk",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
    const response = await fetch(`${baseUrl}/v1/customer/settings/bot-chat`, {
      headers,
    });
    if (!response.ok) {
      return { enabled: false, globalEnabled: false };
    }
    return response.json();
  }

  /**
   * Update bot chat enabled setting for the current user's company.
   * PUT /v1/customer/settings/bot-chat
   *
   * Only Admin or SystemAdministrator can call this.
   */
  updateBotChatSettings(enabled: boolean): Promise<BotAccessResponse> {
    return api
      .put<BotAccessResponse>(
        "/v1/customer/settings/bot-chat",
        { enabled },
        getRequestConfig()
      )
      .then((x) => x.data);
  }

  /**
   * Fetch an artifact image via the bot image proxy.
   * Returns a blob URL for use in <img> src.
   * The caller is responsible for revoking the blob URL when done.
   * Accepts both relative (/api/v1/image/...) and full bot-gateway URLs.
   */
  async fetchBotImage(relativePath: string): Promise<string> {
    const baseUrl = (window as any).app?.config?.apiBaseUrl || "";
    const marker = "/api/v1/image/";
    const markerIdx = relativePath.indexOf(marker);
    const stripped = markerIdx !== -1
      ? relativePath.slice(markerIdx + marker.length)
      : relativePath.replace(/^\/+/, "");
    const url = `${baseUrl}/v1/bot/image/${stripped}`;

    const token = accountContext.accessToken;
    const headers: Record<string, string> = {
      "X-UI-Language": localStorage.getItem("i18nextLng") || "sk",
    };
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error(`Image fetch failed: ${response.status}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  }

  // ─────────────────────────────────────────────────────────────────────
  // Thread CRUD (M4: Chat History)
  // ─────────────────────────────────────────────────────────────────────

  /**
   * List conversation threads for the current user.
   * GET /v1/bot/chat/threads
   *
   * machineId modes:
   *   - undefined → no param (all threads)
   *   - null      → sends ?machine_id=null (platform-only)
   *   - "guid"    → sends ?machine_id=guid (machine-scoped)
   */
  getThreads(limit?: number, offset?: number, machineId?: string | null): Promise<ThreadListResponse> {
    const params: Record<string, unknown> = {};
    if (limit !== undefined) params.limit = limit;
    if (offset !== undefined) params.offset = offset;
    if (machineId !== undefined) {
      params.machine_id = machineId === null ? "null" : machineId;
    }
    return api
      .get<ThreadListResponse>("/v1/bot/chat/threads", {
        ...getRequestConfig(),
        params,
      })
      .then((x) => x.data);
  }

  /**
   * Get thread counts per machine (batch).
   * POST /v1/bot/chat/threads/counts
   */
  getThreadCounts(machineIds: string[]): Promise<ThreadCountsResponse> {
    return api
      .post<ThreadCountsResponse>(
        "/v1/bot/chat/threads/counts",
        { machine_ids: machineIds },
        getRequestConfig()
      )
      .then((x) => x.data);
  }

  /**
   * Create a new conversation thread.
   * POST /v1/bot/chat/threads
   */
  createThread(machineId?: string): Promise<{ id: string; title: string; created_at: string; machine_id?: string }> {
    // MIDAS-BUG-001 (Part 1): use the localised "New chat" label instead of
    // hardcoded English. The title is replaced by the optimistic thread flow
    // once the user sends their first message (with text or image filename).
    const body: Record<string, any> = {
      title: i18n.t("chatBot:newChat", "New Chat"),
    };
    body.machine_id = machineId || null;
    return api
      .post("/v1/bot/chat/threads", body, getRequestConfig())
      .then((x) => x.data);
  }

  /**
   * Get a single thread with its messages.
   * GET /v1/bot/chat/threads/{id}
   */
  getThread(id: string): Promise<ThreadDetailResponse> {
    return api
      .get<ThreadDetailResponse>(`/v1/bot/chat/threads/${id}`, getRequestConfig())
      .then((x) => x.data);
  }

  /**
   * Rename a conversation thread.
   * PATCH /v1/bot/chat/threads/{id}
   */
  renameThread(id: string, title: string): Promise<{ id: string; title: string }> {
    return api
      .patch(`/v1/bot/chat/threads/${id}`, { title }, getRequestConfig())
      .then((x) => x.data);
  }

  /**
   * Delete a conversation thread.
   * DELETE /v1/bot/chat/threads/{id}
   */
  deleteThread(id: string): Promise<void> {
    return api
      .delete(`/v1/bot/chat/threads/${id}`, getRequestConfig())
      .then(() => undefined);
  }

  /**
   * Initialize a MIDAS full-screen session.
   * POST /v1/bot/chat/session-init
   *
   * Returns greeting, summary numbers, and suggestion chips.
   * When machineId is provided, returns machine-scoped data.
   * Falls back to mock data when the bot endpoint is not available yet.
   */
  async sessionInit(machineId?: string): Promise<SessionInitResponse> {
    // BUG-2026-03-20: Mock responses must NOT include a greeting or suggestions
    // when in machine mode. MidasMachineWelcome has its own localized fallback
    // greeting and suggestion cards. Previously, the English mock "Welcome back."
    // was rendered instead of the localized machine greeting when the API
    // returned 429 (rate limited) or was unavailable.
    const mockResponse: SessionInitResponse = machineId
      ? {
          // Machine mode: empty greeting/suggestions so MidasMachineWelcome
          // falls through to its own i18n-based fallback content
          greeting: "",
          summary: {
            open_service_requests: 0,
            pending_offers: 0,
            unread_notifications: 0,
            machines_count: 0,
            recent_activity: null,
          },
          suggestions: [],
        }
      : {
          greeting: "",
          summary: {
            open_service_requests: 0,
            pending_offers: 0,
            unread_notifications: 0,
            machines_count: 0,
            recent_activity: null,
          },
          suggestions: [],
        };

    try {
      const baseUrl = (window as any).app.config.apiBaseUrl;
      const token = accountContext.accessToken;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-UI-Language": localStorage.getItem("i18nextLng") || "sk",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const body: Record<string, any> = {};
      body.machine_id = machineId || null;
      const response = await fetch(`${baseUrl}/v1/bot/chat/session-init`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      });
      if (!response.ok) {
        return mockResponse;
      }
      return response.json();
    } catch {
      return mockResponse;
    }
  }
}

export default new ChatBotApi();
